/**
 * DCTA-142 — the Tencent real-time ASR engine (16k_en) behind DCTA-141's `VoiceInput`.
 *
 * A `Recognizer` like the Web Speech one, so the end-of-speech module (`app/end-of-speech.ts`) and the UX do not
 * change: start / stop / abort, onresult(text, final), onend, onerror. What is different:
 *  - the URL is the BANK's: POST /v1/asr/token returns a 60-second `wss://asr.cloud.tencent.com/asr/v2/…` URL
 *    signed on the server, every parameter inside the signature (16k_en, PCM, VAD at 2000 ms); this app never
 *    holds a key and cannot change a parameter;
 *  - the microphone is ours: an AudioWorklet turns it into 16 kHz 16-bit mono PCM, 40 ms (1280 bytes) a chunk;
 *  - Tencent's VAD splits sentences on a pause but keeps the socket open, so a pause never ends this engine — the
 *    app's end-of-speech decides and `stop()` sends {"type":"end"};
 *  - each sentence (`index`) arrives as slice_type 0 (start), 1 (words so far), 2 (stable); the text handed on is
 *    the stable sentences joined with `stitch` (the full stop at a seam goes, a word repeated across a pause
 *    is said once — the same join the browser engine's pieces get) plus the sentence in progress.
 *
 * The transcript goes to the gateway like typed words (`channel: "voice"`); voice carries zero authority (ADR-0017).
 * Every way out — stop, abort, a refused microphone, a refused ticket, a Tencent error, a dropped socket —
 * releases the microphone, and `onend` fires exactly once.
 */

import { stitch } from "../lib/words.js";
// DCTA-209 F-CSP-1: the URL of the worklet's own hashed file (why a file, not a Blob: pcm16k-worklet.ts).
import workletUrl from "./pcm16k-worklet.ts?worker&url";
import { webSpeechFactory, type Recognizer, type RecognizerFactory } from "./voice.js";

/** What POST /v1/asr/token gives this app (the rest of its answer is documentation). */
export interface AsrTicket {
  readonly url: string;
  readonly voice_id: string;
  readonly expires_at: string;
  readonly max_session_seconds: number;
}

/** The part of a WebSocket the engine uses — a browser socket behind `browserSocket`, a fake in tests. */
export interface AsrSocket {
  readonly readyState: number;
  send(data: string | ArrayBuffer): void;
  close(code?: number): void;
  onopen: (() => void) | null;
  onmessage: ((event: { data: unknown }) => void) | null;
  onclose: ((event: { code: number }) => void) | null;
  onerror: (() => void) | null;
}

/** Starts the microphone; chunks are 1280 bytes of 16 kHz PCM. `stop()` must release the device. */
export type CaptureStarter = (onChunk: (pcm: ArrayBuffer) => void, onError: (code: string) => void) => { stop(): void };

/** The page's microphone pipeline (`micCapture`): a capture starter, and `prepare()` for before the first tap (DCTA-236). */
export type MicCapture = CaptureStarter & { prepare(): void };

export class AsrTicketError extends Error {
  readonly code: string;
  constructor(code: string) {
    super(code);
    this.name = "AsrTicketError";
    this.code = code;
  }
}

const OPEN = 1;
const CLOSED = 3;
/**
 * DCTA-181: everything said from the tap waits for the socket — nothing is dropped (a 2 s cap lost the START of what a
 * customer said whenever the ticket and Tencent's handshake took longer: "Payee can you send money to?" for "To which
 * payee…"). The listening's own 60 s cap bounds it (1,500 chunks, under 2 MB).
 */
/** While catching up, at most twice real time — Tencent's doc asks for 1:1 and calls faster an engine error. */
const DRAIN_PER_CHUNK = 2;
/** One chunk is 40 ms of audio: the pace of the catch-up after Done. */
const CHUNK_MS = 40;
/** DCTA-181: after a listening the capture context keeps running this long, so the next tap captures at once. */
const WARM_MS = 30_000;
const END = JSON.stringify({ type: "end" });
const TICKET_URL = /^wss?:\/\/[^/\s]+\/asr\/v2\//;

// ---------------------------------------------------------------- the ticket

/** POST /v1/asr/token — same origin, the session cookie, the CSRF double-submit, never cached. */
export async function fetchAsrTicket(csrf: string, fetcher: typeof fetch = fetch): Promise<AsrTicket> {
  let response: Response;
  try {
    response = await fetcher("/v1/asr/token", {
      method: "POST",
      headers: { "content-type": "application/json", "x-dcta-csrf": csrf },
      credentials: "same-origin",
      cache: "no-store",
      body: "{}",
    });
  } catch {
    throw new AsrTicketError("network");
  }
  if (!response.ok) {
    const s = response.status;
    throw new AsrTicketError(s === 503 ? "asr-unavailable" : s === 429 ? "asr-rate-limited" : s === 401 || s === 403 ? "asr-auth" : "asr-error");
  }
  let body: unknown;
  try {
    body = await response.json();
  } catch {
    throw new AsrTicketError("asr-error");
  }
  return ticketOf(body);
}

/** Checked field by field: an answer that is not a ticket opens no socket. */
function ticketOf(body: unknown): AsrTicket {
  if (typeof body !== "object" || body === null) throw new AsrTicketError("asr-error");
  const url: unknown = Reflect.get(body, "url");
  const voiceId: unknown = Reflect.get(body, "voice_id");
  const expiresAt: unknown = Reflect.get(body, "expires_at");
  const max: unknown = Reflect.get(body, "max_session_seconds");
  if (typeof url !== "string" || !TICKET_URL.test(url)) throw new AsrTicketError("asr-error");
  if (typeof voiceId !== "string" || voiceId === "" || typeof expiresAt !== "string") throw new AsrTicketError("asr-error");
  if (typeof max !== "number" || !Number.isInteger(max) || max < 1 || max > 300) throw new AsrTicketError("asr-error");
  return { url, voice_id: voiceId, expires_at: expiresAt, max_session_seconds: max };
}

// ---------------------------------------------------------------- the engine

/** Tencent's error codes (tencentcloud.com/document/product/1118/53937) as the app's. */
function tencentCode(code: unknown): string {
  if (code === 4006) return "asr-busy";
  if (code === 4007) return "audio-capture";
  if (code === 4008 || code === 4009) return "network";
  if (typeof code === "number" && code >= 4001 && code <= 4005) return "asr-refused";
  return "asr-error";
}

interface EngineDeps {
  readonly ticket: () => Promise<AsrTicket>;
  readonly capture: CaptureStarter;
  readonly socket: (url: string) => AsrSocket;
  /** DCTA-191: the listening's clip (adapters/pcm-clip.ts) — every chunk Tencent gets is kept there too, for the evidence. */
  readonly clip?: { append(pcm: ArrayBuffer): void } | undefined;
}

class TencentEngine implements Recognizer {
  onresult: ((transcript: string, final: boolean) => void) | undefined;
  onend: (() => void) | undefined;
  onerror: ((code: string) => void) | undefined;
  /** DCTA-181: the loudness of every chunk the microphone gives (the RMS, 0..1) — the bars follow the real voice. */
  onlevel: ((level: number) => void) | undefined;
  readonly levels = true;

  #phase: "idle" | "starting" | "open" | "done" = "idle";
  #endRequested = false;
  #socket: AsrSocket | undefined;
  #capture: { stop(): void } | undefined;
  #queue: ArrayBuffer[] = [];
  /** The stable sentences, joined. */
  #stable = "";
  #cap: ReturnType<typeof setTimeout> | undefined;
  /** After Done: the paced catch-up, then the end frame. */
  #catchUp: ReturnType<typeof setInterval> | undefined;
  #endSent = false;
  #ended = false;

  constructor(private readonly deps: EngineDeps) {}

  start(): void {
    if (this.#phase !== "idle") return;
    this.#phase = "starting";
    // Inside the tap: the microphone and the AudioContext (iOS unlocks audio only in a gesture).
    try {
      this.#capture = this.deps.capture(
        (pcm) => this.#audio(pcm),
        (code) => this.#finish(code),
      );
    } catch {
      queueMicrotask(() => this.#finish("audio-capture"));
      return;
    }
    this.deps.ticket().then(
      (ticket) => this.#connect(ticket),
      (err: unknown) => this.#finish(err instanceof AsrTicketError ? err.code : "asr-error"),
    );
  }

  /** The app's end-of-speech decision: the microphone goes at once; Tencent gets the rest and {"type":"end"}. */
  stop(): void {
    if (this.#phase === "idle" || this.#phase === "done" || this.#endRequested) return;
    this.#endRequested = true;
    this.#releaseMic();
    if (this.#cap !== undefined) clearTimeout(this.#cap);
    if (this.#phase === "open") this.#flushAndEnd();
    // Still connecting: `onopen` sends what waits, then the end.
  }

  /** Cancel: no last words wanted — everything closed now, nothing late reaches the app. */
  abort(): void {
    if (this.#phase === "idle" || this.#phase === "done") return;
    this.#finish();
  }

  #connect(ticket: AsrTicket): void {
    if (this.#phase === "done") return; // aborted or failed while the ticket was on its way
    const socket = this.deps.socket(ticket.url);
    this.#socket = socket;
    socket.onopen = () => {
      if (this.#socket !== socket || this.#phase === "done") return;
      this.#phase = "open";
      if (this.#endRequested) this.#flushAndEnd();
      else this.#drain();
    };
    socket.onmessage = (event) => this.#message(event.data);
    socket.onclose = () => {
      if (this.#socket === socket && this.#phase !== "done") this.#finish("network");
    };
    socket.onerror = () => undefined; // a close follows
    // The floor under the app's own 60 s cap.
    if (!this.#endRequested) this.#cap = setTimeout(() => this.stop(), ticket.max_session_seconds * 1000);
  }

  #audio(pcm: ArrayBuffer): void {
    if (this.#phase === "done" || this.#endRequested) return;
    this.onlevel?.(rms(pcm));
    // DCTA-191 (ADR-0024): what Tencent hears is what the bank keeps — the same chunk, in the same order.
    this.deps.clip?.append(pcm);
    this.#queue.push(pcm);
    if (this.#phase === "open") this.#drain();
  }

  #drain(): void {
    const socket = this.#socket;
    if (socket === undefined || socket.readyState !== OPEN) return;
    for (let i = 0; i < DRAIN_PER_CHUNK; i += 1) {
      const next = this.#queue.shift();
      if (next === undefined) return;
      socket.send(next);
    }
  }

  /**
   * DCTA-181 — Done never discards speech: what still waits goes in order, at most twice real time (the 1:1 rule), and
   * only then the end frame; Tencent's stable sentence and `final: 1` follow it.
   */
  #flushAndEnd(): void {
    const socket = this.#socket;
    if (socket === undefined || socket.readyState !== OPEN || this.#endSent || this.#catchUp !== undefined) return;
    const step = (): void => {
      if (this.#phase === "done" || socket.readyState !== OPEN) {
        this.#stopCatchUp();
        return;
      }
      for (let i = 0; i < DRAIN_PER_CHUNK; i += 1) {
        const next = this.#queue.shift();
        if (next === undefined) break;
        socket.send(next);
      }
      if (this.#queue.length > 0) return;
      this.#stopCatchUp();
      this.#endSent = true;
      socket.send(END);
    };
    step();
    if (!this.#endSent && this.#phase !== "done") this.#catchUp = setInterval(step, CHUNK_MS);
  }

  #stopCatchUp(): void {
    if (this.#catchUp !== undefined) clearInterval(this.#catchUp);
    this.#catchUp = undefined;
  }

  #message(data: unknown): void {
    if (this.#phase === "done") return;
    let message: unknown;
    try {
      message = JSON.parse(String(data));
    } catch {
      this.#finish("asr-error");
      return;
    }
    if (typeof message !== "object" || message === null) {
      this.#finish("asr-error");
      return;
    }
    const code: unknown = Reflect.get(message, "code");
    if (code !== 0) {
      this.#finish(tencentCode(code));
      return;
    }
    const result: unknown = Reflect.get(message, "result");
    if (typeof result === "object" && result !== null) {
      const sliceType: unknown = Reflect.get(result, "slice_type");
      const text: unknown = Reflect.get(result, "voice_text_str");
      if (typeof text === "string") {
        if (sliceType === 2) {
          this.#stable = stitch(this.#stable, text);
          this.onresult?.(this.#stable, true);
        } else if ((sliceType === 0 || sliceType === 1) && text.trim() !== "") {
          this.onresult?.(stitch(this.#stable, text), false);
        }
      }
    }
    if (Reflect.get(message, "final") === 1) this.#finish();
  }

  #releaseMic(): void {
    const capture = this.#capture;
    this.#capture = undefined;
    capture?.stop();
  }

  /** The one way out: microphone released, socket closed, the error (if any) said, `onend` once. */
  #finish(errorCode?: string): void {
    if (this.#phase === "done") return;
    this.#phase = "done";
    if (this.#cap !== undefined) clearTimeout(this.#cap);
    this.#stopCatchUp();
    this.#releaseMic();
    this.#queue = [];
    const socket = this.#socket;
    if (socket !== undefined && socket.readyState !== CLOSED) {
      try {
        socket.close(1000);
      } catch {
        /* already gone */
      }
    }
    if (errorCode !== undefined) this.onerror?.(errorCode);
    queueMicrotask(() => {
      if (this.#ended) return;
      this.#ended = true;
      this.onend?.();
    });
  }
}

/** A chunk's loudness: the RMS of its 16-bit samples, 0..1. */
function rms(pcm: ArrayBuffer): number {
  const samples = new Int16Array(pcm);
  if (samples.length === 0) return 0;
  let sum = 0;
  for (const s of samples) sum += s * s;
  return Math.min(1, Math.sqrt(sum / samples.length) / 32768);
}

/** The browser's WebSocket behind the engine's small interface; binary frames as ArrayBuffer. */
export function browserSocket(url: string): AsrSocket {
  const ws = new WebSocket(url);
  ws.binaryType = "arraybuffer";
  const socket: AsrSocket = {
    get readyState() {
      return ws.readyState;
    },
    send: (data) => ws.send(data),
    close: (code) => ws.close(code),
    onopen: null,
    onmessage: null,
    onclose: null,
    onerror: null,
  };
  ws.onopen = () => socket.onopen?.();
  ws.onmessage = (event) => socket.onmessage?.({ data: event.data });
  ws.onclose = (event) => socket.onclose?.({ code: event.code });
  ws.onerror = () => socket.onerror?.();
  return socket;
}

export interface TencentAsrOptions {
  /** Usually `() => fetchAsrTicket(api.csrf)`. */
  readonly ticket: () => Promise<AsrTicket>;
  readonly capture?: CaptureStarter;
  readonly socket?: (url: string) => AsrSocket;
  /** DCTA-191: the session's clip every engine of a listening appends to (the chat clears and takes it). */
  readonly clip?: { append(pcm: ArrayBuffer): void };
}

/** A new engine per listening, as `VoiceInput` wants (DCTA-89: never reuse a finished engine). */
export function tencentAsrFactory(options: TencentAsrOptions): RecognizerFactory {
  const deps: EngineDeps = {
    ticket: options.ticket,
    capture: options.capture ?? micCapture(),
    socket: options.socket ?? browserSocket,
    clip: options.clip,
  };
  return () => new TencentEngine(deps);
}

// ---------------------------------------------------------------- the flag

export type AsrFlag = "tencent" | "browser";

/**
 * DCTA-162: Tencent's engine is the default — the keys are live (Claude's staging check, 9 Oct). A build opts out with
 * `VITE_ASR=browser`; anything else, or nothing, is Tencent.
 */
export function asrDefault(build: unknown): AsrFlag {
  return build === "browser" ? "browser" : "tencent";
}

/**
 * DCTA-162: when the browser's engine takes over a listening Tencent's could not start — Tencent's is "not here": the bank
 * has no keys (503) or hit the day's cap (429), Tencent refused the signed URL (4001–4005) or is at its concurrency cap
 * (4006, the socket's own 429), or the socket failed. A denied microphone, a signed-out session (asr-auth) or a garbled
 * answer (asr-error) is the app's to show: none of them is a reason to send the customer's voice to another service.
 */
const FALLS_BACK: ReadonlySet<string> = new Set(["asr-unavailable", "asr-rate-limited", "asr-refused", "asr-busy", "network"]);
export function fallsBackToBrowser(code: string): boolean {
  return FALLS_BACK.has(code);
}

/** `?asr=tencent|browser` over the build's default; anything else is the default. */
export function asrFlag(search: string, fallback: AsrFlag): AsrFlag {
  const value = new URLSearchParams(search).get("asr");
  return value === "tencent" || value === "browser" ? value : fallback;
}

/** The engine for the flag: Tencent's only for "tencent", else the browser's (undefined where there is none). */
export function selectRecognizerFactory(flag: AsrFlag, tencent: () => RecognizerFactory): RecognizerFactory | undefined {
  return flag === "tencent" ? tencent() : webSpeechFactory();
}

// ---------------------------------------------------------------- the microphone

/**
 * The browser's microphone as 16 kHz PCM chunks — DCTA-181: ONE pipeline for the page, not one per listening.
 *
 * The AudioContext is made in the first tap (or by `prepare()` after sign-in, below) and kept: every later tap resumes it
 * inside the gesture (iOS starts audio
 * only there; the lock screen, a call or the read-back can leave it suspended), and once the microphone is granted it
 * is resumed again if it is still not running (WebKit lets a page that captures start audio). Its worklet module is
 * asked for in the same tap, beside getUserMedia, and loaded once per context — the first chunk comes as soon as the
 * microphone does. A context the browser closed is replaced in the next tap. The module is `pcm16k-worklet.ts`.
 *
 * What is NOT kept between listenings: the microphone. Each listening asks for it and `stop()` ends its track at once —
 * no microphone indicator between turns, and the bank's read-back never plays through a live, echo-cancelled capture
 * (on iOS and Android that ducks it or sends it to the earpiece). After a listening the context keeps running for
 * WARM_MS — a context that has to start again takes ~0.1–0.15 s (measured, WebKit and Chrome), and what is said meanwhile
 * is lost — then it rests (suspended) until a tap. Chunks reach only the listening that asked for them, never a buffer of
 * the page's.
 *
 * DCTA-236 W1: the stream is wired only once the module has loaded, and a live stream keeps nothing for later — so a module
 * still on its way at the first tap of a page lost what was said meanwhile (prod-verify 15 Oct: beat 4 heard from "100 to
 * Sarah" on; a 2 s load reproduced it on Tencent's real socket). `prepare()`, called after sign-in, makes the context and
 * loads the module then: no microphone and no resume — the context it made rests (suspended) and the tap resumes it inside
 * the gesture, as every later tap does. Once a page; a failed load is forgotten and the tap asks again.
 */
export function micCapture(): MicCapture {
  let ctx: AudioContext | undefined;
  /** The worklet, asked for once per context; forgotten if it failed, so the next tap asks again. */
  let module: Promise<void> | undefined;
  let node: AudioWorkletNode | undefined;
  /** The listening the chunks go to now; none between listenings. */
  let sink: ((pcm: ArrayBuffer) => void) | undefined;
  /** When the context rests, WARM_MS after the last listening. */
  let rest: ReturnType<typeof setTimeout> | undefined;

  const context = (): AudioContext | undefined => {
    if (ctx !== undefined && ctx.state !== "closed") return ctx;
    const Ctor = globalThis.AudioContext ?? (globalThis as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (Ctor === undefined) return undefined;
    ctx = new Ctor();
    module = undefined;
    node = undefined;
    return ctx;
  };

  const worklet = (c: AudioContext): Promise<void> => {
    const loading = c.audioWorklet.addModule(workletUrl);
    loading.catch(() => {
      if (module === loading) module = undefined;
    });
    return loading;
  };
  /** The module for this context: asked for once, by whichever comes first — `prepare()` or a tap. */
  const ready = (c: AudioContext): Promise<void> => (module ??= worklet(c));

  const processor = (c: AudioContext): AudioWorkletNode => {
    const made = new AudioWorkletNode(c, "dcta-pcm16k", { processorOptions: { inputRate: c.sampleRate } });
    made.port.onmessage = (event: MessageEvent<unknown>) => {
      if (event.data instanceof ArrayBuffer) sink?.(event.data);
    };
    // Pulled by the destination (some engines run only connected nodes); the processor writes no sound.
    made.connect(c.destination);
    return made;
  };

  const starter: CaptureStarter = (onChunk, onError) => {
    let c: AudioContext | undefined;
    try {
      c = context();
    } catch {
      c = undefined;
    }
    if (c === undefined) {
      queueMicrotask(() => onError("audio-capture"));
      return { stop: () => undefined };
    }
    const audio = c;
    // Inside the tap: resume, and ask for the worklet beside the microphone.
    if (rest !== undefined) clearTimeout(rest);
    rest = undefined;
    void audio.resume().catch(() => undefined);
    const loading = ready(audio);
    let stopped = false;
    let stream: MediaStream | undefined;
    let source: MediaStreamAudioSourceNode | undefined;
    const mine = (pcm: ArrayBuffer): void => {
      if (!stopped) onChunk(pcm);
    };
    const release = (): void => {
      for (const track of stream?.getTracks() ?? []) track.stop();
      source?.disconnect();
      source = undefined;
      if (sink === mine) sink = undefined;
      if (sink !== undefined) return;
      // The next turn often comes soon: the context stays running for a while, then rests until a tap resumes it.
      if (rest !== undefined) clearTimeout(rest);
      rest = setTimeout(() => {
        rest = undefined;
        if (sink === undefined && audio.state === "running") void audio.suspend().catch(() => undefined);
      }, WARM_MS);
    };
    navigator.mediaDevices
      .getUserMedia({ audio: { channelCount: 1, echoCancellation: true, noiseSuppression: true, autoGainControl: true } })
      .then(async (granted) => {
        stream = granted;
        if (stopped) return release(); // the app stopped while the browser was asking
        await loading;
        if (stopped) return release();
        if (audio.state !== "running") void audio.resume().catch(() => undefined);
        node ??= processor(audio);
        source = audio.createMediaStreamSource(granted);
        source.connect(node);
        sink = mine;
        return undefined;
      })
      .catch((err: unknown) => {
        if (stopped) return;
        stopped = true;
        release();
        onError(err instanceof Error && err.name === "NotAllowedError" ? "not-allowed" : "audio-capture");
      });
    return {
      stop: () => {
        if (stopped) return;
        stopped = true;
        release();
      },
    };
  };
  const prepare = (): void => {
    try {
      const made = ctx === undefined || ctx.state === "closed";
      const c = context();
      if (c === undefined) return;
      // A context made here rests until a tap resumes it inside the gesture: Chromium lets one made after the door's tap run,
      // and a running context is an audio thread awake for a customer who may never speak. One a tap made is left alone.
      if (made) void c.suspend().catch(() => undefined);
      void ready(c);
    } catch {
      // No Web Audio, no AudioWorklet, or a context the browser would not make: the tap tries again and says so.
    }
  };
  return Object.assign(starter, { prepare });
}
